import React, { useState, useRef, useCallback } from "react";
import { Volume2, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Universal "Listen" speaker button — plays the OpenAI TTS for a French phrase.
 * Public endpoint, works without auth (used on landing, dictionary, etc.).
 *
 * Props:
 *   text      — the French phrase to speak (required)
 *   size      — "xs" | "sm" | "md" (default "sm")
 *   variant   — "ghost" | "pill" | "solid" (default "ghost")
 *   className — extra classes for the button
 *   label     — optional visible label (default none)
 */
export default function SpeakButton({
  text,
  size = "sm",
  variant = "ghost",
  className = "",
  label,
  "data-testid": testId,
  ...rest
}) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const sizeCls = {
    xs: "h-6 w-6 text-[10px]",
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm",
  }[size];
  const iconSize = { xs: "h-3 w-3", sm: "h-3.5 w-3.5", md: "h-4 w-4" }[size];

  const variantCls = {
    ghost: "bg-white/5 border border-white/10 text-white hover:bg-white/10",
    pill: "bg-peach-500/15 border border-peach-500/30 text-peach-200 hover:bg-peach-500/25",
    solid: "bg-peach-500 text-navy-900 hover:bg-peach-400",
  }[variant];

  const onClick = useCallback(async (e) => {
    e?.stopPropagation();
    if (!text) return;
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      audioRef.current = null;
      setPlaying(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/tts/speak`, { text, voice: "nova" });
      const a = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
      audioRef.current = a;
      a.onended = () => { setPlaying(false); audioRef.current = null; };
      a.onerror = () => { setPlaying(false); audioRef.current = null; };
      setPlaying(true);
      await a.play();
    } catch (_) {
      /* silent failure — button just returns to idle */
    } finally {
      setLoading(false);
    }
  }, [text]);

  const widthCls = label ? "w-auto px-3 gap-2 flex-row" : sizeCls;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading || !text}
      aria-label={label || "Listen"}
      title={label || "Listen"}
      data-testid={testId || "speak-btn"}
      className={`inline-flex items-center justify-center rounded-full transition ${widthCls} ${variantCls} ${playing ? "ring-2 ring-peach-500/60" : ""} ${className}`}
      {...rest}
    >
      {loading ? (
        <Loader2 className={`${iconSize} animate-spin`} />
      ) : (
        <Volume2 className={iconSize} />
      )}
      {label && <span>{label}</span>}
    </button>
  );
}
